/**
 * Small Zod helpers shared by every schema in this package.
 */
import { z } from 'zod'

/**
 * A schema that validates a non-empty string but types its output as one of the
 * branded IDs from `ids.ts`.
 *
 * Zod's own `.brand()` exists but produces its own `BRAND<>` marker, which would
 * mean maintaining two parallel notions of "branded ID" — one for values that
 * happened to arrive through a Zod parse and one for everything else. Casting
 * here keeps `ids.ts` the single definition. The runtime check is real; only the
 * brand is asserted.
 */
export function idSchema<T extends string>(label: string): z.ZodType<T, z.ZodTypeDef, unknown> {
  return z.string().min(1, `${label} must be a non-empty string`) as unknown as z.ZodType<T, z.ZodTypeDef, unknown>
}

/**
 * ISO-8601 instant, e.g. `2026-08-14T17:30:00.000Z`.
 *
 * Timestamps cross the wire as strings rather than as `Date` objects or Firestore
 * `Timestamp`s, because this contract is shared by a browser, a Node runtime, and
 * a Python service, and a string is the only representation all three agree on.
 * Conversion to a platform-native type happens at the edges.
 */
export const IsoInstantSchema = z
  .string()
  .datetime({ offset: true, message: 'must be an ISO-8601 datetime with a timezone offset or Z' })
export type IsoInstant = z.infer<typeof IsoInstantSchema>

/**
 * ISO-8601 calendar date with no time component, e.g. `1974-03-02`.
 *
 * Clinical dates are genuinely date-only — a diagnosis has a day, not an instant —
 * and forcing them through a datetime introduces a spurious timezone that can
 * shift a birth date across a day boundary and change a patient's computed age.
 */
export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO-8601 date (YYYY-MM-DD)')
export type IsoDate = z.infer<typeof IsoDateSchema>

/** A probability in [0, 1]. */
export const ProbabilitySchema = z.number().min(0).max(1)

/** WGS84 coordinate pair. */
export const GeoPointSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
})
export type GeoPoint = z.infer<typeof GeoPointSchema>

/**
 * A coded clinical concept.
 *
 * `display` is stored alongside the code rather than resolved from a terminology
 * server at read time, deliberately: it is the label the source system used, and
 * showing a coordinator a different label than their EHR shows destroys trust
 * even when our label is technically more correct.
 */
export const CodingSchema = z.object({
  system: z.string().min(1),
  code: z.string().min(1),
  display: z.string().default(''),
})
export type Coding = z.infer<typeof CodingSchema>

/**
 * A quantity with a unit. The unit is required and never defaulted — the entire
 * point of carrying it is to make a mg/dL vs mmol/L mix-up impossible, and a
 * defaulted unit would reintroduce exactly that bug.
 */
export const QuantitySchema = z.object({
  value: z.number(),
  unit: z.string().min(1),
})
export type Quantity = z.infer<typeof QuantitySchema>

/** Narrow an unknown value with a schema, returning `null` instead of throwing. */
export function tryParse<T>(schema: z.ZodType<T, z.ZodTypeDef, unknown>, value: unknown): T | null {
  const result = schema.safeParse(value)
  return result.success ? result.data : null
}

/**
 * Parse an array, dropping and reporting the elements that fail rather than
 * failing the whole batch.
 *
 * Used on every ingest path. A single malformed trial out of 500 should not empty
 * the catalogue, but it must not vanish silently either — the caller gets the
 * rejects and logs them.
 */
export function parseArrayLenient<T>(
  schema: z.ZodType<T, z.ZodTypeDef, unknown>,
  values: readonly unknown[]
): { ok: T[]; rejected: Array<{ index: number; error: string }> } {
  const ok: T[] = []
  const rejected: Array<{ index: number; error: string }> = []
  values.forEach((value, index) => {
    const result = schema.safeParse(value)
    if (result.success) ok.push(result.data)
    else rejected.push({ index, error: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ') })
  })
  return { ok, rejected }
}
